import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { APIError } from '~services/Errors';
import { deepClone } from '~utils/Functions';

import { createList, getCampaignLists, getCampaignListsByNextUrl, toggleLeadsListActiveById } from './api';
import { CampaignLeadList, CampaignLeadListResponse, CreateCampaignLeadList, FilterType } from './domain';

type Options = {
  search?: string;
  filters?: FilterType[];
  shouldFetch?: boolean;
};

const useCampaignListSearch = (campaignId: number, options: Options = {}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [list, setList] = useState<CampaignLeadList[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const { search, filters, shouldFetch = true } = options;
  const stringFilters = JSON.stringify(filters);

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError('');

      let resp: CampaignLeadListResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getCampaignListsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(e instanceof APIError ? e.message : 'Unexpected Error');
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.list]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');

    let resp: CampaignLeadListResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getCampaignLists({
        campaignId,
        search,
        filters,
        cancelToken: axiosCancelRef.current,
      });
    } catch (e) {
      setError('');
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.list);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [search, stringFilters, campaignId]);

  const create = useCallback(
    async (data: CreateCampaignLeadList) => {
      try {
        await createList(campaignId, data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [campaignId, reload],
  );

  const toggleState = useCallback(
    async (listId: number, isActive: boolean) => {
      try {
        await toggleLeadsListActiveById(campaignId, listId, isActive);

        setList((prev) => {
          if (prev) {
            const copy = deepClone(prev);
            const index = copy.findIndex((c) => c.id === listId);
            if (index !== -1) {
              copy[index].isActive = isActive;
              return copy;
            }
          }

          return prev;
        });
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      return isActive;
    },
    [campaignId, reload],
  );

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  useEffect(() => {
    setList([]);
  }, [search, stringFilters, shouldFetch]);

  useEffect(() => {
    const load = async (search?: string, filter?: FilterType[]) => {
      setLoading(true);
      setError('');

      let resp: CampaignLeadListResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        // resp = await getCampaigns(search, filters, accessFilterId, axiosCancelRef.current);
        resp = await getCampaignLists({
          campaignId,
          search,
          filters,
          cancelToken: axiosCancelRef.current,
        });
      } catch (e) {
        setError(e instanceof APIError ? e.message : 'Unexpected Error');
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.list]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search, filters);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, stringFilters, shouldFetch]);

  return { loading, error, list, hasMore, intersectionObserverRef, create, toggleState };
};

export default useCampaignListSearch;
